<template>
  <tr v-if="!compact" class="row" :class="{ 'is-inactive': !category.isActive }">
    <td class="cell-name">
      <div class="name-cell">
        <span class="style-chip" :style="{ background: chipBackground(categoryColor) }">
          <v-icon size="16" :color="categoryColor">{{ categoryShapeIcon }}</v-icon>
        </span>
        <span class="name-text">{{ category.label }}</span>
        <span v-if="!category.isActive" class="status-badge status-inactive">Inactive</span>
      </div>
    </td>
    <td class="cell-desc">{{ category.description || '—' }}</td>
    <td class="cell-actions">
      <DisplayOverlayWindowButton ref="detailsWindow" buttonText="Details" windowTitle="Category Information">
        <div class="category-details">
          <div class="details-eyebrow">Marker Category</div>
          <h2 class="details-title">{{ category.label }}</h2>
          <p class="details-desc">{{ category.description || 'No description provided.' }}</p>
          <div class="details-meta">
            <span class="meta-label">Status</span>
            <span class="status-badge" :class="category.isActive ? 'status-active' : 'status-inactive'">
              {{ category.isActive ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <template v-if="isEditable">
            <StyleEditor v-model:shape="editShape" v-model:color="editColor" :shape-items="shapeItems"
              :color-swatches="colorSwatches" :same-color="sameColor" :saving="isUpdating" @save="saveStyle" />

            <div class="details-actions">
              <button v-if="category.isActive" class="btn-danger" :disabled="isUpdating" @click="deleteCategory">
                {{ isUpdating ? 'Working…' : 'Delete' }}
              </button>
              <button v-else class="btn-primary" :disabled="isUpdating" @click="reactivateCategory">
                {{ isUpdating ? 'Working…' : 'Reactivate' }}
              </button>
            </div>
          </template>

          <div v-else class="readonly-note">
            <v-icon size="16" color="#8aa0aa">mdi-lock-outline</v-icon>
            <span>This is a shared default category and can't be edited.</span>
          </div>
        </div>
      </DisplayOverlayWindowButton>
    </td>
  </tr>

  <article v-else class="card" :class="{ 'is-inactive': !category.isActive }">
    <div class="card-head">
      <span class="style-chip" :style="{ background: chipBackground(categoryColor) }">
        <v-icon size="16" :color="categoryColor">{{ categoryShapeIcon }}</v-icon>
      </span>
      <span class="card-name">{{ category.label }}</span>
      <span v-if="!category.isActive" class="status-badge status-inactive">Inactive</span>
    </div>
    <p v-if="category.description" class="card-desc">{{ category.description }}</p>
    <div class="card-actions">
      <DisplayOverlayWindowButton ref="detailsWindow" buttonText="Details" windowTitle="Category Information">
        <div class="category-details">
          <div class="details-eyebrow">Marker Category</div>
          <h2 class="details-title">{{ category.label }}</h2>
          <p class="details-desc">{{ category.description || 'No description provided.' }}</p>
          <div class="details-meta">
            <span class="meta-label">Status</span>
            <span class="status-badge" :class="category.isActive ? 'status-active' : 'status-inactive'">
              {{ category.isActive ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <template v-if="isEditable">
            <StyleEditor v-model:shape="editShape" v-model:color="editColor" :shape-items="shapeItems"
              :color-swatches="colorSwatches" :same-color="sameColor" :saving="isUpdating" @save="saveStyle" />

            <div class="details-actions">
              <button v-if="category.isActive" class="btn-danger" :disabled="isUpdating" @click="deleteCategory">
                {{ isUpdating ? 'Working…' : 'Delete' }}
              </button>
              <button v-else class="btn-primary" :disabled="isUpdating" @click="reactivateCategory">
                {{ isUpdating ? 'Working…' : 'Reactivate' }}
              </button>
            </div>
          </template>

          <div v-else class="readonly-note">
            <v-icon size="16" color="#8aa0aa">mdi-lock-outline</v-icon>
            <span>This is a shared default category and can't be edited.</span>
          </div>
        </div>
      </DisplayOverlayWindowButton>
    </div>
  </article>
</template>

<script setup>
import { defineEmits, defineOptions, defineProps, computed, ref } from 'vue';
import UpdateMarkerTypeModel from '@/domain/marker/UpdateMarkerTypeModel';
import MarkerService from '@/services/MarkerService';
import DisplayOverlayWindowButton from '@/components/buttons/DisplayOverlayWindowButton.vue';
import StyleEditor from './MarkerCategoryStyleEditor.vue';
import { useOrganizationStore } from '@/store/organizationStore';
import { MARKER_SHAPES, MARKER_COLOR_SWATCHES, DEFAULT_MARKER_SHAPE, DEFAULT_MARKER_COLOR } from '@/lib/markerShapeFactory';

defineOptions({ name: "MarkerCategoryTableItem" })

const props = defineProps({
  category: Object,
  compact: { type: Boolean, default: false }
})

const emits = defineEmits(['onCategoryModified'])

const organizationStore = useOrganizationStore();
const detailsWindow = ref(null)
const isUpdating = ref(false)

const shapeItems = MARKER_SHAPES;
const colorSwatches = MARKER_COLOR_SWATCHES;
const sameColor = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

// Edit state for this row's category style (each row is its own component instance).
const editShape = ref(props.category.shape || DEFAULT_MARKER_SHAPE);
const editColor = ref(props.category.color || DEFAULT_MARKER_COLOR);

// Global/default categories (shared across orgs) are read-only; only org-specific ones edit.
const isEditable = computed(() => props.category.isOrganizationSpecific === true);

const categoryColor = computed(() => props.category.color || DEFAULT_MARKER_COLOR);
const categoryShapeIcon = computed(
  () => MARKER_SHAPES.find(s => s.value === props.category.shape)?.icon || 'mdi-circle-outline'
);
const chipBackground = (hex) => `${hex}22`;

const deleteCategory = async () => { await updateCategory(false); };
const reactivateCategory = async () => { await updateCategory(true); };

const saveStyle = async () => {
  await persist(props.category.isActive, editColor.value, editShape.value);
};

// Toggling active must keep the current style (otherwise a merge could drop it).
const updateCategory = async (isActive) => {
  await persist(isActive, props.category.color, props.category.shape);
};

const persist = async (isActive, color, shape) => {
  isUpdating.value = true;
  const updateRecord = new UpdateMarkerTypeModel(props.category.id, organizationStore.organizationId,
    props.category.label, props.category.description, isActive, props.category.eTag, color, shape);
  try {
    await MarkerService.updateMarkerType(updateRecord);
  } finally {
    isUpdating.value = false;
    emits('onCategoryModified')
    detailsWindow.value?.closeWindow();
  }
}
</script>

<style scoped>
.row {
  transition: background 0.15s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.row:last-child { border-bottom: none; }
.row:hover { background: rgba(100, 255, 218, 0.04); }

.row.is-inactive .cell-name,
.row.is-inactive .cell-desc {
  opacity: 0.5;
}

.row td {
  padding: 12px 16px;
  vertical-align: middle;
  font-size: 0.92rem;
}

.cell-name { color: #fff; font-weight: 600; }
.cell-desc { color: #b0bec5; }
.cell-actions { width: 120px; text-align: right; white-space: nowrap; }

.name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.style-chip {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.card {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.card.is-inactive .card-head,
.card.is-inactive .card-desc {
  opacity: 0.6;
}

.card-head {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-name {
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  flex: 1 1 auto;
}

.card-desc {
  font-size: 0.88rem;
  color: #b0bec5;
  margin: 0;
  line-height: 1.45;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.status-active {
  background: rgba(74, 222, 128, 0.12);
  border: 1px solid rgba(74, 222, 128, 0.3);
  color: #4ade80;
}

.status-inactive {
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
}

.category-details {
  padding: 8px;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.details-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa0aa;
}

.details-title {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  color: #fff;
}

.details-desc {
  color: #b0bec5;
  margin: 0;
  line-height: 1.55;
}

.details-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}

.meta-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #8aa0aa;
  font-weight: 700;
}

.details-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.readonly-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  color: #8aa0aa;
  font-size: 0.85rem;
}

.btn-primary {
  padding: 10px 18px;
  border-radius: 10px;
  background: var(--duallo-accent-gradient);
  color: #071514;
  font-weight: 700;
  border: none;
  cursor: pointer;
  font: inherit;
}

.btn-danger {
  padding: 10px 18px;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
  font-weight: 700;
  cursor: pointer;
  font: inherit;
}

.btn-primary:disabled,
.btn-danger:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
